import React, { useState, useEffect, useMemo } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import { Task } from '../types/task';
import { format, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  FolderIcon,
  BriefcaseIcon,
  CalendarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Spinner, Badge } from './ui';

type GroupBy = 'project' | 'business' | 'week' | 'month';

interface TaskGroup {
  key: string;
  label: string;
  tasks: Task[];
  count: number;
}

const CompletedTasksView: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<GroupBy>('week');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchCompletedTasks = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/api/tasks/', {
          params: {
            is_done: true,
            ordering: '-completion_date',
            page_size: 1000 // Get all completed tasks
          }
        });
        const results = Array.isArray(res.data) ? res.data : res.data.results || [];
        setTasks(results.filter((task: Task) => task.is_done === true));
      } catch (err) {
        console.error('Error fetching completed tasks:', err);
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCompletedTasks();
  }, []);

  const groupedTasks = useMemo(() => {
    const groups: TaskGroup[] = [];

    if (groupBy === 'project') {
      // Group by project
      const projectMap = new Map<string, Task[]>();
      tasks.forEach(task => {
        const key = task.project?.id.toString() || 'no-project';
        if (!projectMap.has(key)) {
          projectMap.set(key, []);
        }
        projectMap.get(key)!.push(task);
      });
      projectMap.forEach((tasks, key) => {
        const label = tasks[0]?.project?.name || 'No Project';
        groups.push({ key, label, tasks, count: tasks.length });
      });
    } else if (groupBy === 'business') {
      // Group by business
      const businessMap = new Map<string, Task[]>();
      tasks.forEach(task => {
        const key = task.business?.id.toString() || 'no-business';
        if (!businessMap.has(key)) {
          businessMap.set(key, []);
        }
        businessMap.get(key)!.push(task);
      });
      businessMap.forEach((tasks, key) => {
        const label = tasks[0]?.business?.name || 'No Business';
        groups.push({ key, label, tasks, count: tasks.length });
      });
    } else if (groupBy === 'week') {
      // Group by week
      const weekMap = new Map<string, Task[]>();
      tasks.forEach(task => {
        if (task.completion_date) {
          const completionDate = parseISO(task.completion_date);
          const weekStart = startOfWeek(completionDate, { weekStartsOn: 1 }); // Monday
          const key = format(weekStart, 'yyyy-MM-dd');
          if (!weekMap.has(key)) {
            weekMap.set(key, []);
          }
          weekMap.get(key)!.push(task);
        }
      });
      // Sort by week (most recent first)
      const sortedWeeks = Array.from(weekMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));
      sortedWeeks.forEach(([key, tasks]) => {
        const completionDate = parseISO(tasks[0].completion_date!);
        const weekStart = startOfWeek(completionDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(completionDate, { weekStartsOn: 1 });
        const label = `Week of ${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
        groups.push({ key, label, tasks, count: tasks.length });
      });
    } else if (groupBy === 'month') {
      // Group by month
      const monthMap = new Map<string, Task[]>();
      tasks.forEach(task => {
        if (task.completion_date) {
          const completionDate = parseISO(task.completion_date);
          const key = format(completionDate, 'yyyy-MM');
          if (!monthMap.has(key)) {
            monthMap.set(key, []);
          }
          monthMap.get(key)!.push(task);
        }
      });
      // Sort by month (most recent first)
      const sortedMonths = Array.from(monthMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));
      sortedMonths.forEach(([key, tasks]) => {
        const label = format(parseISO(tasks[0].completion_date!), 'MMMM yyyy');
        groups.push({ key, label, tasks, count: tasks.length });
      });
    }

    return groups;
  }, [tasks, groupBy]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(groupedTasks.map(g => g.key)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[48]
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        paddingTop: SPACING[48],
        paddingBottom: SPACING[48]
      }}>
        <CheckCircleIcon style={{
          margin: '0 auto',
          height: '48px',
          width: '48px',
          color: COLORS.text.tertiary
        }} />
        <h3 style={{
          marginTop: SPACING[8],
          fontSize: TYPOGRAPHY.size.sm,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary
        }}>No completed tasks</h3>
        <p style={{
          marginTop: SPACING[4],
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.text.secondary
        }}>Complete some tasks to see them here.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[16]
        }}>
          <label style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.secondary
          }}>Group by:</label>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            style={{
              display: 'block',
              width: '160px',
              paddingLeft: SPACING[12],
              paddingRight: SPACING[40],
              paddingTop: SPACING[8],
              paddingBottom: SPACING[8],
              fontSize: TYPOGRAPHY.size.sm,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              outline: 'none',
              backgroundColor: COLORS.bg.panel
            }}
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="project">Project</option>
            <option value="business">Business</option>
          </select>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING[8]
        }}>
          <button
            onClick={expandAll}
            style={{
              fontSize: TYPOGRAPHY.size.sm,
              color: COLORS.accent.default,
              fontWeight: TYPOGRAPHY.weight.medium,
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Expand All
          </button>
          <span style={{ color: COLORS.border.default }}>|</span>
          <button
            onClick={collapseAll}
            style={{
              fontSize: TYPOGRAPHY.size.sm,
              color: COLORS.accent.default,
              fontWeight: TYPOGRAPHY.weight.medium,
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{
        backgroundColor: COLORS.bg.base,
        borderRadius: RADIUS,
        padding: SPACING[16]
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: SPACING[16]
        }}>
          <div>
            <p style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Total Completed</p>
            <p style={{
              fontSize: TYPOGRAPHY.size['5xl'],
              fontWeight: TYPOGRAPHY.weight.semibold,
              color: COLORS.text.primary
            }}>{tasks.length}</p>
          </div>
          <div>
            <p style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Groups</p>
            <p style={{
              fontSize: TYPOGRAPHY.size['5xl'],
              fontWeight: TYPOGRAPHY.weight.semibold,
              color: COLORS.text.primary
            }}>{groupedTasks.length}</p>
          </div>
          <div>
            <p style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>With Project</p>
            <p style={{
              fontSize: TYPOGRAPHY.size['5xl'],
              fontWeight: TYPOGRAPHY.weight.semibold,
              color: COLORS.text.primary
            }}>
              {tasks.filter(t => t.project).length}
            </p>
          </div>
          <div>
            <p style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>With Business</p>
            <p style={{
              fontSize: TYPOGRAPHY.size['5xl'],
              fontWeight: TYPOGRAPHY.weight.semibold,
              color: COLORS.text.primary
            }}>
              {tasks.filter(t => t.business).length}
            </p>
          </div>
        </div>
      </div>

      {/* Grouped Tasks */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
        {groupedTasks.map(group => (
          <div key={group.key} style={{
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            overflow: 'hidden'
          }}>
            {/* Group Header */}
            <button
              onClick={() => toggleGroup(group.key)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: SPACING[16],
                paddingRight: SPACING[16],
                paddingTop: SPACING[12],
                paddingBottom: SPACING[12],
                backgroundColor: COLORS.bg.base,
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING[12]
              }}>
                {groupBy === 'project' && <FolderIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />}
                {groupBy === 'business' && <BriefcaseIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />}
                {(groupBy === 'week' || groupBy === 'month') && <CalendarIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />}
                <span style={{
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.primary
                }}>{group.label}</span>
                <Badge variant="default" size="sm">{group.count} tasks</Badge>
              </div>
              {expandedGroups.has(group.key) ? (
                <ChevronUpIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
              ) : (
                <ChevronDownIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
              )}
            </button>

            {/* Group Content */}
            {expandedGroups.has(group.key) && (
              <div>
                {group.tasks.map((task, index) => (
                  <div key={task.id} style={{
                    paddingLeft: SPACING[16],
                    paddingRight: SPACING[16],
                    paddingTop: SPACING[12],
                    paddingBottom: SPACING[12],
                    borderTop: index === 0 ? `1px solid ${COLORS.bg.hover}` : 'none',
                    borderBottom: index < group.tasks.length - 1 ? `1px solid ${COLORS.bg.hover}` : 'none'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          fontSize: TYPOGRAPHY.size.sm,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          color: COLORS.text.primary
                        }}>{task.description}</p>
                        <div style={{
                          marginTop: SPACING[4],
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: SPACING[8],
                          fontSize: TYPOGRAPHY.size.xs,
                          color: COLORS.text.secondary
                        }}>
                          {task.completion_date && (
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              <CheckCircleIcon style={{
                                height: '14px',
                                width: '14px',
                                marginRight: SPACING[4],
                                color: COLORS.status.success
                              }} />
                              {format(parseISO(task.completion_date), 'MMM d, yyyy h:mm a')}
                            </span>
                          )}
                          {task.project && groupBy !== 'project' && (
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              <FolderIcon style={{
                                height: '14px',
                                width: '14px',
                                marginRight: SPACING[4]
                              }} />
                              {task.project.name}
                            </span>
                          )}
                          {task.business && groupBy !== 'business' && (
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              <BriefcaseIcon style={{
                                height: '14px',
                                width: '14px',
                                marginRight: SPACING[4]
                              }} />
                              {task.business.name}
                            </span>
                          )}
                        </div>
                        {task.categories.length > 0 && (
                          <div style={{
                            marginTop: SPACING[8],
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: SPACING[4]
                          }}>
                            {task.categories.map(cat => (
                              <Badge key={cat.id} variant="default" size="sm">
                                {cat.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompletedTasksView;
